import { Autocomplete, TextField } from '@mui/material';
import type { ContentSummary } from '@augment-toolkit/core';
import { useGetCatalogQuery } from '../../api/content.api';

type MethodPickerProps = {
  label: string;
  /** Confluence page ids. */
  value: string[];
  onChange: (ids: string[]) => void;
};

/**
 * Methods from the catalog, **by name and chosen by id**: the library links an Agent Skill to
 * a method by its Confluence page id, which survives a rename, and nobody should have to
 * know one. Only L3 methods are offered, because a link to a Process is a link to a
 * method that does not exist.
 *
 * The catalog is the one search already caches, so this costs no request of its own.
 */
export const MethodPicker = ({ label, value, onChange }: MethodPickerProps) => {
  const { data, isLoading } = useGetCatalogQuery();
  const methods = (data ?? []).filter((item) => item.type === 'Method');
  const selected = value.flatMap((id) => {
    const found = methods.find((method) => method.id === id);
    return found === undefined ? [] : [found];
  });

  return (
    <Autocomplete<ContentSummary, true>
      multiple
      options={methods}
      loading={isLoading}
      value={selected}
      getOptionLabel={(method) => method.name}
      getOptionKey={(method) => method.id}
      isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
      onChange={(_event, chosen) => {
        onChange(chosen.map((method) => method.id));
      }}
      renderInput={(params) => <TextField {...params} label={label} helperText="The methods this Agent Skill helps with." />}
    />
  );
};
